"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateWorldpayReceiptHTML = void 0;
const homi_shared_1 = require("@getgreenline/homi-shared");
const moment_1 = __importDefault(require("moment"));
const enums_1 = require("../../global/enums");
const ReceiptDictionary_1 = require("../contracts/internal/receipt/ReceiptDictionary");
const enums_2 = require("../enums");
const getDeclinedTransactionMessage_1 = require("./getDeclinedTransactionMessage");
const getReceiptDictionary = (response) => {
    var _a;
    const language = (_a = response.language) !== null && _a !== void 0 ? _a : enums_1.Language.ENGLISH;
    return ReceiptDictionary_1.ReceiptDictionary[language];
};
const getTransactionType = (response) => {
    const { transactionType } = response;
    const isSale = transactionType === enums_2.TransactionTypes.SALE;
    return isSale
        ? getReceiptDictionary(response).TransactionTypes.sale
        : getReceiptDictionary(response).TransactionTypes.refund;
};
const getTransactionDateTime = (response) => {
    const { transactionDateTime } = response;
    return `${(0, moment_1.default)(transactionDateTime).local().format('YYYY-MM-DD HH:mm:ss')}\n`;
};
const getPaymentTypeAndLabel = (response) => {
    const { cardLogo, paymentType, emv } = response;
    const isInterac = cardLogo === 'Interac';
    const paymentTypeAndLabel = emv ? `${paymentType} ${emv.applicationLabel}` : paymentType;
    return isInterac ? paymentTypeAndLabel : cardLogo;
};
const getApplicationIdentifier = (response) => {
    if (!response.emv) {
        return;
    }
    return response.emv.applicationIdentifier;
};
const getLogo = (response) => {
    const { accountType, cardLogo } = response;
    const isInterac = cardLogo === 'Interac';
    let logoLine = cardLogo;
    if (isInterac) {
        const interactLogoLines = [];
        cardLogo && interactLogoLines.push(cardLogo);
        switch (accountType) {
            case 'Checking':
                interactLogoLines.push(getReceiptDictionary(response).ExtraLabels.checking);
                break;
            case 'Savings':
                interactLogoLines.push(getReceiptDictionary(response).ExtraLabels.savings);
                break;
            default:
                accountType && interactLogoLines.push(accountType);
                break;
        }
        logoLine = interactLogoLines.join(' / ');
    }
    return logoLine;
};
const getResponseCode = (response) => {
    const { expressResponseCode, hostResponseCode } = response._processor;
    const responseCodes = [];
    expressResponseCode && responseCodes.push(expressResponseCode);
    hostResponseCode && responseCodes.push(hostResponseCode);
    return responseCodes.join(' / ');
};
const getResponseMessage = (response) => {
    const { expressResponseMessage } = response._processor;
    let responseMessage = expressResponseMessage === null || expressResponseMessage === void 0 ? void 0 : expressResponseMessage.toLocaleLowerCase();
    switch (responseMessage) {
        case 'approved':
            responseMessage = getReceiptDictionary(response).ExtraLabels.approved;
            break;
        case 'partial approved':
            responseMessage = getReceiptDictionary(response).ExtraLabels.partiallyApproved;
            break;
        case 'declined':
        case 'invalid card info':
        case 'duplicate':
        case 'call issuer':
        case 'cardNumber required':
            responseMessage = getReceiptDictionary(response).ExtraLabels.declined;
            break;
        default:
            responseMessage = getReceiptDictionary(response).ExtraLabels.declined;
    }
    return responseMessage;
};
const getDeclinedLogMessage = (response) => {
    if (response.isApproved) {
        return;
    }
    return (0, getDeclinedTransactionMessage_1.getDeclinedTransactionMessage)(response);
};
const getTotal = (response) => {
    const { approvedAmount, isApproved, totalAmount } = response;
    let centValue = 0;
    if (isApproved && approvedAmount !== undefined) {
        centValue = approvedAmount * 100;
    }
    if (!approvedAmount && totalAmount !== undefined) {
        centValue = totalAmount * 100;
    }
    const amount = (0, homi_shared_1.formatCurrencyFromCents)(centValue);
    return `${getReceiptDictionary(response).WorldpayLabels.approvedAmount}: ${amount}`;
};
const getUserVerificationSection = (response) => {
    if (!response.isApproved) {
        return;
    }
    const { pinVerified } = response;
    return pinVerified
        ? {
            pinVerified: `${getReceiptDictionary(response).WorldpayLabels.pinVerified}`,
        }
        : {
            signatureRequired: `${getReceiptDictionary(response).ExtraLabels.customerSignature}`,
        };
};
const getFooter = (response) => {
    const label = response.isApproved
        ? getReceiptDictionary(response).ExtraLabels.agreement + '\n\n'
        : '';
    return label + getReceiptDictionary(response).ExtraLabels.thankYou;
};
const getReceiptType = (response, receiptOption) => {
    const dictionary = getReceiptDictionary(response);
    switch (receiptOption) {
        case enums_2.WorldpayReceiptOption.MERCHANT:
            return dictionary.ExtraLabels.merchantCopy;
        case enums_2.WorldpayReceiptOption.CUSTOMER:
            return dictionary.ExtraLabels.customerCopy;
        default:
            return dictionary.ExtraLabels.reprint;
    }
};
const generateWorldpayReceiptHTML = (company, location, response, receiptOption, style) => {
    const { receiptAddressText, receiptLocationNameText } = location.receiptSettings;
    const fontSize = {
        default: style && style.fontSize ? style.fontSize.default : '1.5rem',
        large: style && style.fontSize ? style.fontSize.large : '3rem',
    };
    const dictionary = getReceiptDictionary(response);
    const paymentTypeAndLabel = getPaymentTypeAndLabel(response);
    const applicationIdentifier = getApplicationIdentifier(response);
    const logo = getLogo(response);
    const responseCodes = getResponseCode(response);
    const responseMessage = getResponseMessage(response);
    const declinedLogMessage = getDeclinedLogMessage(response);
    const total = getTotal(response);
    const { pinVerified, signatureRequired } = getUserVerificationSection(response) || {};
    const footer = getFooter(response);
    const receiptType = getReceiptType(response, receiptOption);
    const { merchantId, terminalId, referenceNumber, ticketNumber, accountNumber, entryMode, wpTransactionNumber, approvalNumber, emv, isApproved, } = response;
    return `
    <div id='worldpay-print-section' style='padding-left: 10px; padding-right: 10px; padding-bottom: 10px; font-size: ${fontSize.default}'>
      <div style='text-align: center;'>
        <div><b style='font-weight: 600;'>${company.name}</b></div>
        ${receiptLocationNameText ? `<div>${receiptLocationNameText}</div>` : ''}
        ${receiptAddressText ? `<div>${receiptAddressText}</div>` : ''}
      </div>

      <div style='text-align: center; margin-top: 8px; margin-bottom: 8px;'>
        <div>
          <b style='font-size: ${fontSize.large}; fong-weight: 600;'>${getTransactionType(response)}</b>
        </div>
        <div>${getTransactionDateTime(response)}</div>
        ${paymentTypeAndLabel ? `<div>${paymentTypeAndLabel}</div>` : ''}
          ${applicationIdentifier ? `<div>${applicationIdentifier}</div>` : ''}
      </div>

      <table style='width: 100%; border: none; border-width: 0; border-collapse: collapse; padding: 0; font-size: ${fontSize.default}''>
        ${merchantId
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.WorldpayLabels.merchantId}</td>
              <td style='text-align: right;'>${merchantId}</td>
            </tr>`
        : ''}
        ${terminalId
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.WorldpayLabels.terminalId}</td>
              <td style='text-align: right;'>${terminalId}</td>
            </tr>`
        : ''}
        ${referenceNumber
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.ExtraLabels.referenceNumber}</td>
              <td style='text-align: right;'>${referenceNumber}</td>
            </tr>`
        : ''}
        ${ticketNumber
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.ExtraLabels.ticketNumber}</td>
              <td style='text-align: right;'>${ticketNumber}</td>
            </tr>`
        : ''}
        ${accountNumber
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.WorldpayLabels.accountNumber}</td>
              <td style='text-align: right;'>${accountNumber}</td>
            </tr>`
        : ''}
        ${logo
        ? `
            <tr>
              <td style='white-space: nowrap;'>Logo</td>
              <td style='text-align: right;'>${logo}</td>
            </tr>`
        : ''}
        ${entryMode
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.WorldpayLabels.entryMode}</td>
              <td style='text-align: right;'>${entryMode}</td>
            </tr>`
        : ''}
        ${wpTransactionNumber
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.ExtraLabels.wpTransactionNumber}</td>
              <td style='text-align: right;'>${wpTransactionNumber}</td>
            </tr>`
        : ''}
        ${approvalNumber
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.WorldpayLabels.approvalNumber}</td>
              <td style='text-align: right;'>${approvalNumber}</td>
            </tr>`
        : ''}
        ${responseCodes
        ? `
            <tr>
              <td style='white-space: nowrap;'>${dictionary.ExtraLabels.responseCode}</td>
              <td style='text-align: right;'>${responseCodes}</td>
            </tr>`
        : ''}
      </table>

      <div style='text-align: center; margin-top: 8px; margin-bottom: 8px;'>
        <div style='
          ${!isApproved ? 'background-color: black; color: white;' : ''}'
        >
          <b style='font-size: ${fontSize.large}; fong-weight: 600;'>
            ${isApproved ? responseMessage : ` ${responseMessage} `}
          </b>
        </div>
        ${declinedLogMessage ? `<div>${declinedLogMessage}</div>` : ''}
        ${(emv === null || emv === void 0 ? void 0 : emv.cryptogram) ? `<div>${emv.cryptogram}</div>` : ''}
      </div>

      <div style='text-align: center; margin-top: 8px; margin-bottom: 8px;'>
        <b style='font-size: ${fontSize.large}; fong-weight: 600;'>
          ${total}
        </b>
      </div>

      ${pinVerified
        ? `<div style='text-align: center; margin-bottom: 8px;'>${pinVerified}</div>`
        : ''}

      ${signatureRequired && receiptOption !== enums_2.WorldpayReceiptOption.CUSTOMER
        ? `
          <div style='text-align: center; margin-bottom: 8px;'>
            <div>${dictionary.ExtraLabels.customerSignature}</div>
            <div style='margin-top: 68px;'>X_______________________</div>
          </div>`
        : ''}

        <div style='text-align: center; white-space: pre-line;  margin-bottom: 24px;'>${footer}</div>

        <div style='text-align: center;'>
          ${receiptType}
        </div>
    </div>
  `;
};
exports.generateWorldpayReceiptHTML = generateWorldpayReceiptHTML;
//# sourceMappingURL=generateWorldpayReceiptHTML.js.map